import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Stack, Plus, Trash, Printer, WarningCircle, Gear, CheckCircle } from '@phosphor-icons/react';
import CartonDiagram, { skuColour } from '../components/CartonDiagram';
import { formatNzDate } from '../utils/datetime';
import { getDefaultPrinter, sendZpl } from '../lib/zebraPrint';
import { buildPlanZpl } from '../lib/labelZpl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const kg = (g) => `${((g || 0) / 1000).toFixed(2)} kg`;

// ------------------------------------------------------------------
// Manual entry row — a searchable SKU + qty input.
// ------------------------------------------------------------------
const ManualRow = ({ row, catalogueSkus, onChange, onRemove }) => (
  <div className="flex gap-2 mb-2">
    <input
      list="pack-sku-list"
      value={row.sku}
      onChange={(e) => onChange({ ...row, sku: e.target.value.toUpperCase() })}
      placeholder="SKU (e.g. PRO-CHO-1000K)"
      data-testid={`pack-manual-sku-${row.id}`}
      className="flex-1 px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary text-sm focus:border-gold focus:outline-none font-mono"
    />
    <datalist id="pack-sku-list">
      {catalogueSkus.map((s) => <option key={s} value={s} />)}
    </datalist>
    <input
      type="number"
      min={1}
      value={row.qty}
      onChange={(e) => onChange({ ...row, qty: e.target.value })}
      placeholder="Qty"
      data-testid={`pack-manual-qty-${row.id}`}
      className="w-24 px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary text-sm focus:border-gold focus:outline-none"
    />
    <button
      onClick={onRemove}
      aria-label="Remove row"
      data-testid={`pack-manual-remove-${row.id}`}
      className="px-2 text-text-secondary hover:text-danger"
    >
      <Trash size={16} weight="bold" />
    </button>
  </div>
);

// ------------------------------------------------------------------
// Per-carton result card with SVG diagram + pick list.
// ------------------------------------------------------------------
const CartonCard = ({ carton, index, sharedMaxMm }) => {
  const [layerIdx, setLayerIdx] = useState(0);
  const totalTubs = carton.contents.reduce((s, c) => s + c.qty, 0);
  return (
    <div
      className="bg-surface border border-border-color rounded-xl p-4 print:break-inside-avoid"
      data-testid={`pack-carton-card-${index}`}
    >
      <div className="flex items-start justify-between mb-2 gap-3 flex-wrap">
        <div>
          <p className="text-sm text-text-secondary">Carton {index + 1}</p>
          <h3 className="text-xl font-bold text-gold" data-testid={`pack-carton-name-${index}`}>
            {carton.cartonName}
            <span className="text-text-secondary text-xs font-normal ml-2 font-mono">
              {carton.lengthMm} × {carton.widthMm} × {carton.heightMm} mm
            </span>
          </h3>
        </div>
        <div className="text-right text-xs text-text-secondary">
          <p>{totalTubs} tubs · <span className="text-text-primary font-semibold" data-testid={`pack-carton-total-weight-${index}`}>{kg(carton.totalWeightG)}</span></p>
          {carton.emptyWeightG > 0 && (
            <p className="text-[10px]">
              (tubs {kg(carton.contentsWeightG)} + carton {kg(carton.emptyWeightG)})
            </p>
          )}
          <p>{carton.layers.length} layer{carton.layers.length === 1 ? '' : 's'} · <span className="capitalize text-gold">{carton.layers[layerIdx]?.layout}</span> layout</p>
        </div>
      </div>

      {/* Pick list */}
      <ul className="mb-3 space-y-1" data-testid={`pack-carton-picklist-${index}`}>
        {carton.contents.map((c) => (
          <li key={c.sku} className="flex items-center gap-2 text-sm">
            <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: skuColour(c.sku) }} />
            <span className="font-mono text-text-primary">{c.sku}</span>
            <span className="ml-auto font-bold text-gold">× {c.qty}</span>
          </li>
        ))}
      </ul>

      {/* Layer switcher */}
      {carton.layers.length > 1 && (
        <div className="flex gap-1 mb-2" data-testid={`pack-carton-layer-switch-${index}`}>
          {carton.layers.map((_, i) => (
            <button
              key={i}
              onClick={() => setLayerIdx(i)}
              className={`px-2 py-1 text-xs rounded ${i === layerIdx ? 'bg-gold text-black font-bold' : 'bg-black border border-border-color text-text-secondary hover:border-gold'}`}
              data-testid={`pack-carton-layer-btn-${index}-${i}`}
            >
              Layer {i + 1}
            </button>
          ))}
        </div>
      )}

      {/* Top-down diagram */}
      <div className="flex justify-center">
        <CartonDiagram carton={carton} layerIndex={layerIdx} sharedMaxMm={sharedMaxMm} />
      </div>
      <p className="text-[10px] text-text-secondary text-center mt-1 print:hidden">
        Top-down view · grid lines every 100 mm
      </p>

      {/* Batch number record lines — printed sheet only */}
      <div className="hidden print:block mt-4" data-testid={`pack-carton-batch-lines-${index}`}>
        <p className="text-[10px] uppercase tracking-wider text-black mb-2 font-semibold">
          Batch numbers
        </p>
        {carton.contents.map((c) => (
          <div key={c.sku} className="flex items-end gap-3 mb-4">
            <span className="font-mono text-xs text-black shrink-0">{c.sku}</span>
            <span className="flex-1 border-b border-black h-4" />
          </div>
        ))}
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// Admin catalogue editor — collapsible; only shown when the Gear is clicked.
// ------------------------------------------------------------------
const CatalogueEditor = ({ onClose, onRefresh }) => {
  const [products, setProducts] = useState([]);
  const [cartons, setCartons] = useState([]);
  const [tab, setTab] = useState('products');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const load = useCallback(async () => {
    const [p, c] = await Promise.all([
      axios.get(`${API}/packing/products`),
      axios.get(`${API}/packing/cartons`),
    ]);
    setProducts(p.data || []);
    setCartons(c.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveProduct = async (row) => {
    setSaving(true); setNotice(null);
    try {
      await axios.put(`${API}/packing/products/${encodeURIComponent(row.sku)}`, {
        sku: row.sku,
        heightMm: Number(row.heightMm),
        diameterMm: Number(row.diameterMm),
        grossWeightG: Number(row.grossWeightG),
        packDiameterMm: row.packDiameterMm ? Number(row.packDiameterMm) : null,
        packHeightMm: row.packHeightMm ? Number(row.packHeightMm) : null,
        sealedBoxQty: row.sealedBoxQty ? Number(row.sealedBoxQty) : null,
        active: row.active !== false,
      });
      setNotice({ kind: 'ok', text: `Saved ${row.sku}.` });
      onRefresh?.();
    } catch (e) { setNotice({ kind: 'err', text: e?.response?.data?.detail || 'Save failed' }); }
    finally { setSaving(false); }
  };
  const deleteProduct = async (sku) => {
    if (!window.confirm(`Delete packing data for ${sku}?`)) return;
    await axios.delete(`${API}/packing/products/${encodeURIComponent(sku)}`);
    load(); onRefresh?.();
  };
  const saveCarton = async (row) => {
    setSaving(true); setNotice(null);
    try {
      const body = {
        name: row.name,
        internalLengthMm: Number(row.internalLengthMm),
        internalWidthMm: Number(row.internalWidthMm),
        internalHeightMm: Number(row.internalHeightMm),
        maxWeightG: row.maxWeightG ? Number(row.maxWeightG) : null,
        emptyWeightG: Number(row.emptyWeightG || 0),
        sortOrder: Number(row.sortOrder || 0),
        active: row.active !== false,
      };
      if (row.id) await axios.put(`${API}/packing/cartons/${row.id}`, body);
      else await axios.post(`${API}/packing/cartons`, body);
      setNotice({ kind: 'ok', text: `Saved ${row.name}.` });
      load(); onRefresh?.();
    } catch (e) { setNotice({ kind: 'err', text: e?.response?.data?.detail || 'Save failed' }); }
    finally { setSaving(false); }
  };
  const deleteCarton = async (row) => {
    if (!window.confirm(`Delete carton "${row.name}"?`)) return;
    await axios.delete(`${API}/packing/cartons/${row.id}`);
    load(); onRefresh?.();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <div
        className="bg-surface border border-border-color rounded-xl w-full max-w-5xl p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="pack-catalogue-editor"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-bold text-gold flex items-center gap-2">
            <Gear size={20} weight="duotone" /> Packing catalogue
          </h3>
          <button onClick={onClose} className="text-text-secondary hover:text-gold" data-testid="pack-catalogue-close">Close</button>
        </div>
        <div className="flex gap-2 mb-3">
          <button
            onClick={() => setTab('products')}
            data-testid="pack-catalogue-tab-products"
            className={`px-3 py-1.5 rounded text-sm ${tab === 'products' ? 'bg-gold text-black font-bold' : 'bg-black border border-border-color text-text-secondary'}`}
          >Products ({products.length})</button>
          <button
            onClick={() => setTab('cartons')}
            data-testid="pack-catalogue-tab-cartons"
            className={`px-3 py-1.5 rounded text-sm ${tab === 'cartons' ? 'bg-gold text-black font-bold' : 'bg-black border border-border-color text-text-secondary'}`}
          >Cartons ({cartons.length})</button>
        </div>
        {notice && (
          <p className={`text-xs mb-3 px-2 py-1.5 rounded border ${notice.kind === 'ok' ? 'text-success bg-success/10 border-success/30' : 'text-danger bg-danger/10 border-danger/30'}`}>
            {notice.text}
          </p>
        )}

        <div className="flex items-center justify-end mb-2">
          <label className="inline-flex items-center gap-2 text-[11px] text-text-secondary cursor-pointer select-none" data-testid="pack-catalogue-advanced-toggle-label">
            <input
              type="checkbox"
              checked={showAdvanced}
              onChange={(e) => setShowAdvanced(e.target.checked)}
              className="accent-gold"
              data-testid="pack-catalogue-advanced-toggle"
            />
            Show advanced fields
          </label>
        </div>

        {tab === 'products' && (
          <div>
            <div className={`grid ${showAdvanced ? 'grid-cols-[1.3fr_repeat(6,1fr)_130px]' : 'grid-cols-[1.3fr_repeat(3,1fr)_130px]'} gap-2 text-xs text-text-secondary font-bold mb-1 px-1`}>
              <div className="pl-2">SKU</div>
              <div className="text-center">Diameter (mm)</div>
              <div className="text-center">Height (mm)</div>
              <div className="text-center">Weight (g)</div>
              {showAdvanced && <div className="text-center">Pack Ø (mm)</div>}
              {showAdvanced && <div className="text-center">Pack H (mm)</div>}
              {showAdvanced && <div className="text-center">Box qty</div>}
              <div className="text-center">Active / Save</div>
            </div>
            {products.map((row, idx) => (
              <ProductRow key={row.sku} row={row}
                showAdvanced={showAdvanced}
                onSave={(next) => saveProduct(next)}
                onDelete={() => deleteProduct(row.sku)}
                onChange={(next) => setProducts((prev) => prev.map((p, i) => i === idx ? next : p))}
                busy={saving} />
            ))}
            {showAdvanced && (
              <p className="mt-2 text-[10px] text-text-secondary max-w-2xl">
                <span className="text-gold">Pack Ø / Pack H</span> are effective packing dimensions used for both layout math and the diagram. Leave blank to use the physical Diameter / Height. Reduce them when tubs actually pack tighter than their nominal size (e.g. 148 mm 1kg tubs use pack Ø = 141 mm). <span className="text-gold">Box qty</span> is the production sealed box quantity — orders at/above it ship as untouched sealed boxes (6 × 2kg, 8 × 1kg, 28 × 350–425g). Leave blank for no restriction (100g tubs, TABs).
              </p>
            )}
            <button
              onClick={() => setProducts([...products, { sku: '', diameterMm: 0, heightMm: 0, grossWeightG: 0, active: true, _new: true }])}
              data-testid="pack-add-product"
              className="mt-2 inline-flex items-center gap-1 text-xs text-gold hover:underline"
            >
              <Plus size={12} weight="bold" /> Add product
            </button>
          </div>
        )}

        {tab === 'cartons' && (
          <div>
            <div className={`grid ${showAdvanced ? 'grid-cols-[1.4fr_repeat(5,1fr)_0.5fr_100px]' : 'grid-cols-[1.4fr_repeat(3,1fr)_100px]'} gap-2 text-xs text-text-secondary font-bold mb-1 px-1`}>
              <div className="pl-2">Name</div>
              <div className="text-center">L (mm)</div>
              <div className="text-center">W (mm)</div>
              <div className="text-center">H (mm)</div>
              {showAdvanced && <div className="text-center">Max wt (g)</div>}
              {showAdvanced && <div className="text-center">Empty wt (g)</div>}
              {showAdvanced && <div className="text-center">Sort</div>}
              <div className="text-center">Save</div>
            </div>
            {cartons.map((row, idx) => (
              <CartonRow key={row.id || idx} row={row}
                showAdvanced={showAdvanced}
                onSave={(next) => saveCarton(next)}
                onDelete={() => deleteCarton(row)}
                onChange={(next) => setCartons((prev) => prev.map((c, i) => i === idx ? next : c))}
                busy={saving} />
            ))}
            {showAdvanced && (
              <p className="mt-2 text-[10px] text-text-secondary max-w-2xl">
                <span className="text-gold">Empty wt</span> is the tare weight of the empty carton in grams. It&apos;s added
                to the tub weight so the printed total is the actual weight the picker lifts and courier charges for.
              </p>
            )}
            <button
              onClick={() => setCartons([...cartons, { name: '', internalLengthMm: 0, internalWidthMm: 0, internalHeightMm: 0, maxWeightG: null, emptyWeightG: 0, sortOrder: cartons.length + 1, active: true, _new: true }])}
              data-testid="pack-add-carton"
              className="mt-2 inline-flex items-center gap-1 text-xs text-gold hover:underline"
            >
              <Plus size={12} weight="bold" /> Add carton
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const ProductRow = ({ row, showAdvanced, onSave, onDelete, onChange, busy }) => (
  <div className={`grid ${showAdvanced ? 'grid-cols-[1.3fr_repeat(6,1fr)_130px]' : 'grid-cols-[1.3fr_repeat(3,1fr)_130px]'} gap-2 mb-1 items-center`}>
    <input value={row.sku} onChange={(e) => onChange({ ...row, sku: e.target.value.toUpperCase() })}
      className="min-w-0 px-2 py-1.5 bg-black border border-border-color rounded text-text-primary text-xs font-mono focus:border-gold focus:outline-none" />
    <input type="number" value={row.diameterMm} onChange={(e) => onChange({ ...row, diameterMm: e.target.value })}
      className="min-w-0 px-2 py-1.5 bg-black border border-border-color rounded text-text-primary text-xs text-center focus:border-gold focus:outline-none" />
    <input type="number" value={row.heightMm} onChange={(e) => onChange({ ...row, heightMm: e.target.value })}
      className="min-w-0 px-2 py-1.5 bg-black border border-border-color rounded text-text-primary text-xs text-center focus:border-gold focus:outline-none" />
    <input type="number" value={row.grossWeightG} onChange={(e) => onChange({ ...row, grossWeightG: e.target.value })}
      className="min-w-0 px-2 py-1.5 bg-black border border-border-color rounded text-text-primary text-xs text-center focus:border-gold focus:outline-none" />
    {showAdvanced && (
      <input type="number" value={row.packDiameterMm || ''} onChange={(e) => onChange({ ...row, packDiameterMm: e.target.value })}
        placeholder="—"
        className="min-w-0 px-2 py-1.5 bg-black border border-border-color rounded text-text-primary text-xs text-center focus:border-gold focus:outline-none" />
    )}
    {showAdvanced && (
      <input type="number" value={row.packHeightMm || ''} onChange={(e) => onChange({ ...row, packHeightMm: e.target.value })}
        placeholder="—"
        className="min-w-0 px-2 py-1.5 bg-black border border-border-color rounded text-text-primary text-xs text-center focus:border-gold focus:outline-none" />
    )}
    {showAdvanced && (
      <input type="number" value={row.sealedBoxQty || ''} onChange={(e) => onChange({ ...row, sealedBoxQty: e.target.value })}
        placeholder="—" data-testid={`pack-product-boxqty-${row.sku}`}
        className="min-w-0 px-2 py-1.5 bg-black border border-border-color rounded text-text-primary text-xs text-center focus:border-gold focus:outline-none" />
    )}
    <div className="min-w-0 flex gap-1 items-center justify-center">
      <input type="checkbox" checked={row.active !== false} onChange={(e) => onChange({ ...row, active: e.target.checked })} className="accent-gold" />
      <button onClick={() => onSave(row)} disabled={busy} className="px-2 py-1 text-[10px] font-bold bg-gold text-black rounded disabled:opacity-50">Save</button>
      <button onClick={onDelete} className="text-danger px-1" aria-label="Delete"><Trash size={12} weight="bold" /></button>
    </div>
  </div>
);

const CartonRow = ({ row, showAdvanced, onSave, onDelete, onChange, busy }) => (
  <div className={`grid ${showAdvanced ? 'grid-cols-[1.4fr_repeat(5,1fr)_0.5fr_100px]' : 'grid-cols-[1.4fr_repeat(3,1fr)_100px]'} gap-2 mb-1 items-center`}>
    <input value={row.name} onChange={(e) => onChange({ ...row, name: e.target.value })}
      className="min-w-0 px-2 py-1.5 bg-black border border-border-color rounded text-text-primary text-xs focus:border-gold focus:outline-none" />
    <input type="number" value={row.internalLengthMm} onChange={(e) => onChange({ ...row, internalLengthMm: e.target.value })}
      className="min-w-0 px-2 py-1.5 bg-black border border-border-color rounded text-text-primary text-xs text-center focus:border-gold focus:outline-none" />
    <input type="number" value={row.internalWidthMm} onChange={(e) => onChange({ ...row, internalWidthMm: e.target.value })}
      className="min-w-0 px-2 py-1.5 bg-black border border-border-color rounded text-text-primary text-xs text-center focus:border-gold focus:outline-none" />
    <input type="number" value={row.internalHeightMm} onChange={(e) => onChange({ ...row, internalHeightMm: e.target.value })}
      className="min-w-0 px-2 py-1.5 bg-black border border-border-color rounded text-text-primary text-xs text-center focus:border-gold focus:outline-none" />
    {showAdvanced && (
      <input type="number" value={row.maxWeightG || ''} onChange={(e) => onChange({ ...row, maxWeightG: e.target.value })}
        className="min-w-0 px-2 py-1.5 bg-black border border-border-color rounded text-text-primary text-xs text-center focus:border-gold focus:outline-none" placeholder="—" />
    )}
    {showAdvanced && (
      <input type="number" value={row.emptyWeightG || ''} onChange={(e) => onChange({ ...row, emptyWeightG: e.target.value })}
        className="min-w-0 px-2 py-1.5 bg-black border border-border-color rounded text-text-primary text-xs text-center focus:border-gold focus:outline-none" placeholder="0" />
    )}
    {showAdvanced && (
      <input type="number" value={row.sortOrder || 0} onChange={(e) => onChange({ ...row, sortOrder: e.target.value })}
        className="min-w-0 px-2 py-1.5 bg-black border border-border-color rounded text-text-primary text-xs text-center focus:border-gold focus:outline-none" />
    )}
    <div className="min-w-0 flex gap-1 items-center justify-center">
      <button onClick={() => onSave(row)} disabled={busy} className="px-2 py-1 text-[10px] font-bold bg-gold text-black rounded disabled:opacity-50">Save</button>
      {row.id && <button onClick={onDelete} className="text-danger px-1" aria-label="Delete"><Trash size={12} weight="bold" /></button>}
    </div>
  </div>
);

// ------------------------------------------------------------------
// Box label sticker — one 4"×6" sticker per carton for the GC420d
// thermal printer. Rendered off-screen and only shown when the user
// clicks "Print box labels" (a body class swaps display in print CSS).
// ------------------------------------------------------------------
const BoxLabelSticker = ({ plan, carton, index, total }) => {
  const src = plan.source || {};
  let title = 'Packing list';
  let sub = 'Manual pack';
  if (src.type === 'order') {
    title = src.orderNumber || src.id || 'Order';
    sub = [src.customerName, src.customerCode].filter(Boolean).join(' · ') || '—';
  } else if (src.type === 'saver') {
    title = src.saverName || `Saver ${src.saverNumber || ''}`.trim();
    sub = [src.customerName, src.customerCode].filter(Boolean).join(' · ') || '—';
  }
  const totalTubs = carton.contents.reduce((s, c) => s + c.qty, 0);
  return (
    <div
      className="label-sticker"
      data-testid={`pack-box-label-${index}`}
    >
      <div className="label-top">
        <div className="label-title">{title}</div>
        <div className="label-sub">{sub}</div>
      </div>

      <div className="label-boxnum">
        <span className="label-boxnum-big">Box {index + 1}</span>
        <span className="label-boxnum-of">of {total}</span>
      </div>

      <div className="label-carton-line">
        {carton.cartonName} · {carton.lengthMm}×{carton.widthMm}×{carton.heightMm} mm
      </div>

      <table className="label-contents">
        <thead>
          <tr><th>SKU</th><th className="qty">Qty</th></tr>
        </thead>
        <tbody>
          {carton.contents.map((c) => (
            <tr key={c.sku}>
              <td>{c.sku}</td>
              <td className="qty">{c.qty}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="label-footer">
        <span>{totalTubs} tubs · {kg(carton.totalWeightG)}{total > 1 ? ` · ship total ${kg(plan.totalShipmentWeightG)}` : ''}</span>
        <span>{formatNzDate(new Date())}</span>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// Main Pack Assistant page.
// ------------------------------------------------------------------
export default function PackAssistantPage() {
  const [source, setSource] = useState('manual'); // 'manual' | 'order' | 'saver'
  const [manualRows, setManualRows] = useState([{ id: 1, sku: '', qty: 1 }]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [savers, setSavers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [catalogueOpen, setCatalogueOpen] = useState(false);
  const [catalogueSkus, setCatalogueSkus] = useState([]);

  const loadCatalogueSkus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/packing/products`);
      setCatalogueSkus((data || []).filter((p) => p.active !== false).map((p) => p.sku).sort());
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadCatalogueSkus(); }, [loadCatalogueSkus]);

  useEffect(() => {
    if (source === 'order') {
      axios.get(`${API}/submitted-orders?limit=100`)
        .then((r) => setOrders(r.data?.items || []))
        .catch(() => setOrders([]));
    } else if (source === 'saver') {
      axios.get(`${API}/users`)
        .then((r) => setCustomers((r.data || []).filter((u) => u.role === 'customer' && u.active !== false)))
        .catch(() => setCustomers([]));
    }
    setSelectedCustomerId('');
    setSelectedId('');
    setSavers([]);
    setPlan(null);
  }, [source]);

  useEffect(() => {
    if (source !== 'saver' || !selectedCustomerId) { setSavers([]); return; }
    axios.get(`${API}/saved-bundles?customerId=${selectedCustomerId}`)
      .then((r) => setSavers(r.data || []))
      .catch(() => setSavers([]));
    setSelectedId('');
  }, [source, selectedCustomerId]);

  const runPlan = async () => {
    setBusy(true); setError(null); setPlan(null);
    try {
      let res;
      if (source === 'manual') {
        const items = manualRows
          .filter((r) => r.sku.trim() && Number(r.qty) > 0)
          .map((r) => ({ sku: r.sku.trim(), qty: Number(r.qty) }));
        if (items.length === 0) { setError('Add at least one SKU + quantity.'); setBusy(false); return; }
        res = await axios.post(`${API}/packing/plan`, { items });
      } else if (source === 'order') {
        if (!selectedId) { setError('Pick an order to pack.'); setBusy(false); return; }
        res = await axios.get(`${API}/packing/plan/from-order/${selectedId}`);
      } else {
        if (!selectedId) { setError('Pick a saver to pack.'); setBusy(false); return; }
        res = await axios.get(`${API}/packing/plan/from-saver/${selectedId}`);
      }
      setPlan(res.data);
    } catch (e) { setError(e?.response?.data?.detail || 'Pack plan failed'); }
    finally { setBusy(false); }
  };

  const summaryByCartonName = useMemo(() => {
    const map = {};
    (plan?.cartons || []).forEach((c) => { map[c.cartonName] = (map[c.cartonName] || 0) + 1; });
    return map;
  }, [plan]);

  // Print the per-carton 4"×6" box labels for a GC420d thermal printer.
  // We inject an @page rule sized to the label stock, add a body class
  // that swaps what's visible in print CSS, print, then clean up.
  const printBoxLabels = () => {
    const styleEl = document.createElement('style');
    styleEl.id = 'labels-print-page';
    styleEl.textContent =
      '@media print { @page { size: 102mm 152mm; margin: 0; } }';
    document.head.appendChild(styleEl);
    document.body.classList.add('labels-print-mode');
    // Give the browser a tick to apply styles before opening the dialog.
    setTimeout(() => {
      window.print();
      document.body.classList.remove('labels-print-mode');
      styleEl.remove();
    }, 80);
  };

  // -------- Zebra Browser Print integration --------
  // Detected default printer (populated on mount and refresh).
  const [zebraPrinter, setZebraPrinter] = useState(null);
  const [zebraProbed, setZebraProbed] = useState(false);
  const [zebraSending, setZebraSending] = useState(false);
  const [zebraNotice, setZebraNotice] = useState(null); // { kind: 'ok'|'err', text }

  const refreshZebra = useCallback(async () => {
    const p = await getDefaultPrinter();
    setZebraPrinter(p);
    setZebraProbed(true);
  }, []);

  useEffect(() => { refreshZebra(); }, [refreshZebra]);

  const sendToZebra = async () => {
    if (!plan?.cartons?.length) return;
    setZebraSending(true);
    setZebraNotice(null);
    try {
      let device = zebraPrinter;
      if (!device) {
        device = await getDefaultPrinter();
        setZebraPrinter(device);
      }
      if (!device) {
        setZebraNotice({
          kind: 'err',
          text: 'Zebra Browser Print not reachable. Make sure it\u2019s running and this domain is in its Accepted Hosts.',
        });
        return;
      }
      const zpls = buildPlanZpl(plan, formatNzDate(new Date()));
      // Send each label as its own job so Browser Print/queue can retry
      // individual failures without re-printing the whole batch.
      for (const zpl of zpls) {
        const ok = await sendZpl(device, zpl);
        if (!ok) throw new Error('Browser Print rejected a label.');
      }
      setZebraNotice({
        kind: 'ok',
        text: `Sent ${zpls.length} label${zpls.length === 1 ? '' : 's'} to ${device.name || 'Zebra printer'}.`,
      });
    } catch (e) {
      setZebraNotice({ kind: 'err', text: e.message || 'Failed to send to Zebra.' });
    } finally {
      setZebraSending(false);
    }
  };

  return (
    <div className="flex-1 p-8 print:p-2" data-testid="pack-assistant-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-start justify-between gap-4 mb-6 print:hidden">
          <div>
            <h1 className="text-4xl font-bold font-heading text-text-primary mb-1 flex items-center gap-3">
              <Stack size={34} weight="duotone" className="text-gold" /> Pack Assistant
            </h1>
            <p className="text-text-secondary">
              Fewest-carton packing plan with top-down layer diagrams. Supports grid, hex and mixed-diameter nesting.
            </p>
          </div>
          <button
            onClick={() => setCatalogueOpen(true)}
            data-testid="pack-open-catalogue"
            className="shrink-0 flex items-center gap-2 px-4 py-2.5 border border-gold text-gold font-bold rounded-lg hover:bg-gold hover:text-black"
          >
            <Gear size={18} weight="bold" /> Packing catalogue
          </button>
        </div>

        {/* Source picker */}
        <div className="bg-surface border border-border-color rounded-xl p-4 mb-4 print:hidden" data-testid="pack-source-panel">
          <div className="flex flex-wrap gap-2 mb-3">
            {['manual', 'order', 'saver'].map((s) => (
              <button
                key={s}
                onClick={() => setSource(s)}
                data-testid={`pack-source-${s}`}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${source === s ? 'bg-gold text-black' : 'bg-black border border-border-color text-text-secondary hover:border-gold'}`}
              >
                {s === 'manual' ? 'Manual list' : s === 'order' ? 'From Submitted Order' : 'From Saved Saver'}
              </button>
            ))}
          </div>

          {source === 'manual' && (
            <div>
              {manualRows.map((row, idx) => (
                <ManualRow
                  key={row.id}
                  row={row}
                  catalogueSkus={catalogueSkus}
                  onChange={(next) => setManualRows((prev) => prev.map((r, i) => i === idx ? next : r))}
                  onRemove={() => setManualRows((prev) => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx))}
                />
              ))}
              <button
                onClick={() => setManualRows((prev) => [...prev, { id: Date.now(), sku: '', qty: 1 }])}
                data-testid="pack-add-manual-row"
                className="inline-flex items-center gap-1 text-xs text-gold hover:underline"
              >
                <Plus size={12} weight="bold" /> Add another SKU
              </button>
            </div>
          )}

          {source === 'order' && (
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              data-testid="pack-source-order-select"
              className="w-full px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary focus:border-gold focus:outline-none"
            >
              <option value="">— Select a submitted order —</option>
              {orders.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.orderNumber || o.id} · {o.customerSnapshot?.name || o.customerId || 'customer'} · {o.bundleCount || 0} saver(s) · {o.looseItemCount || 0} loose
                </option>
              ))}
            </select>
          )}

          {source === 'saver' && (
            <div className="space-y-2">
              <select
                value={selectedCustomerId}
                onChange={(e) => setSelectedCustomerId(e.target.value)}
                data-testid="pack-source-saver-customer-select"
                className="w-full px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary focus:border-gold focus:outline-none"
              >
                <option value="">— Select a customer —</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                ))}
              </select>
              {selectedCustomerId && (
                <select
                  value={selectedId}
                  onChange={(e) => setSelectedId(e.target.value)}
                  data-testid="pack-source-saver-select"
                  className="w-full px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary focus:border-gold focus:outline-none"
                >
                  <option value="">— Select a saved saver —</option>
                  {savers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name || `Saver ${s.number}`} · {(s.products || []).length} product(s)
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          <div className="mt-3 flex gap-2">
            <button
              onClick={runPlan}
              disabled={busy}
              data-testid="pack-run-button"
              className="px-4 py-2 bg-gold text-black text-sm font-bold rounded-lg hover:bg-gold-hover disabled:opacity-50"
            >
              {busy ? 'Packing…' : 'Plan pack'}
            </button>
            {plan && (
              <button
                onClick={() => window.print()}
                data-testid="pack-print-button"
                className="px-4 py-2 border border-gold text-gold text-sm font-semibold rounded-lg hover:bg-gold hover:text-black inline-flex items-center gap-1"
              >
                <Printer size={14} weight="bold" /> Print plan
              </button>
            )}
            {plan && (
              <button
                onClick={printBoxLabels}
                data-testid="pack-print-labels-button"
                className="px-4 py-2 border border-gold text-gold text-sm font-semibold rounded-lg hover:bg-gold hover:text-black inline-flex items-center gap-1"
                title="Print one 4×6 thermal sticker per box via the browser dialog"
              >
                <Printer size={14} weight="bold" /> Print box labels
              </button>
            )}
            {plan && (
              <button
                onClick={sendToZebra}
                disabled={zebraSending || !zebraPrinter}
                data-testid="pack-zebra-print-button"
                title={zebraPrinter
                  ? `Send native ZPL to ${zebraPrinter.name}`
                  : 'Zebra Browser Print not detected. Install & run it, and add this domain to Accepted Hosts.'}
                className="px-4 py-2 bg-gold text-black text-sm font-bold rounded-lg hover:bg-gold-hover disabled:opacity-40 inline-flex items-center gap-1"
              >
                <Printer size={14} weight="bold" />
                {zebraSending ? 'Sending…' : 'Send to Zebra'}
              </button>
            )}
          </div>

          {/* Zebra printer status pill */}
          <div className="mt-3 flex items-center gap-2 text-xs" data-testid="zebra-status">
            <span
              className={`inline-block w-2 h-2 rounded-full ${
                !zebraProbed ? 'bg-text-secondary' :
                zebraPrinter ? 'bg-success' : 'bg-danger'
              }`}
            />
            {!zebraProbed && <span className="text-text-secondary">Checking Zebra printer…</span>}
            {zebraProbed && zebraPrinter && (
              <span className="text-text-secondary">
                Zebra ready: <span className="text-gold font-semibold">{zebraPrinter.name}</span>
                {zebraPrinter.connection ? ` (${zebraPrinter.connection})` : ''}
              </span>
            )}
            {zebraProbed && !zebraPrinter && (
              <span className="text-text-secondary">
                Zebra Browser Print not detected.{' '}
                <button onClick={refreshZebra} className="text-gold underline" data-testid="zebra-retry">Retry</button>
              </span>
            )}
          </div>
          {zebraNotice && (
            <p
              className={`mt-2 px-3 py-2 text-xs rounded border ${zebraNotice.kind === 'ok'
                ? 'text-success bg-success/10 border-success/30'
                : 'text-danger bg-danger/10 border-danger/30'}`}
              data-testid="zebra-notice"
            >
              {zebraNotice.text}
            </p>
          )}
          {error && (
            <p className="mt-3 px-3 py-2 text-xs text-danger bg-danger/10 border border-danger/30 rounded" data-testid="pack-error">
              <WarningCircle size={12} weight="bold" className="inline mr-1" /> {error}
            </p>
          )}
        </div>

        {/* Results */}
        {plan && (
          <>
            {/* Plan header — identifies which order/saver this pack plan is for.
                Visible on screen and in print so warehouse pickers know whose
                pallet they're looking at. */}
            <div
              className="bg-surface border border-border-color rounded-xl p-4 mb-3 print:mb-2 print:border-black/40"
              data-testid="pack-plan-header"
            >
              {plan.source && plan.source.type === 'order' && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-text-secondary print:text-black">Packing plan for order</p>
                  <p className="text-lg font-bold text-gold print:text-black" data-testid="pack-plan-header-order-number">
                    {plan.source.orderNumber || plan.source.id}
                  </p>
                  <p className="text-sm text-text-primary print:text-black" data-testid="pack-plan-header-customer">
                    {plan.source.customerName || '—'}
                    {plan.source.customerCode ? ` · ${plan.source.customerCode}` : ''}
                  </p>
                  {plan.source.submittedAt && (
                    <p className="text-xs text-text-secondary print:text-black">
                      Submitted {formatNzDate(plan.source.submittedAt)} · Printed {formatNzDate(new Date())}
                    </p>
                  )}
                </div>
              )}
              {plan.source && plan.source.type === 'saver' && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-text-secondary print:text-black">Packing plan for saved saver</p>
                  <p className="text-lg font-bold text-gold print:text-black" data-testid="pack-plan-header-saver-name">
                    {plan.source.saverName || `Saver ${plan.source.saverNumber || plan.source.id}`}
                  </p>
                  <p className="text-sm text-text-primary print:text-black" data-testid="pack-plan-header-customer">
                    {plan.source.customerName || '—'}
                    {plan.source.customerCode ? ` · ${plan.source.customerCode}` : ''}
                  </p>
                  <p className="text-xs text-text-secondary print:text-black">
                    Printed {formatNzDate(new Date())}
                  </p>
                </div>
              )}
              {!plan.source && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-text-secondary print:text-black">Packing plan</p>
                  <p className="text-sm text-text-primary print:text-black">Manual pack list — not linked to an order</p>
                  <p className="text-xs text-text-secondary print:text-black">Printed {formatNzDate(new Date())}</p>
                </div>
              )}
            </div>

            <div className="bg-surface border border-gold rounded-xl p-4 mb-4 print:mb-2" data-testid="pack-summary">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <CheckCircle size={16} weight="bold" className="text-success" />
                <span className="text-sm font-bold text-gold">
                  {plan.totalCartons} carton{plan.totalCartons === 1 ? '' : 's'} needed
                </span>
                {plan.totalShipmentWeightG > 0 && (
                  <span
                    className="ml-auto text-sm font-bold text-text-primary print:text-black"
                    data-testid="pack-shipment-total-weight"
                  >
                    Total shipment: {kg(plan.totalShipmentWeightG)}
                    {plan.totalEmptyWeightG > 0 && (
                      <span className="ml-1 text-xs text-text-secondary print:text-black font-normal">
                        (tubs {kg(plan.totalContentsWeightG)} + cartons {kg(plan.totalEmptyWeightG)})
                      </span>
                    )}
                  </span>
                )}
              </div>
              <p className="text-xs text-text-secondary">
                {Object.entries(summaryByCartonName).map(([n, c]) => `${c}× ${n}`).join(' · ')}
              </p>
              {plan.unpackable && plan.unpackable.length > 0 && (
                <p className="mt-2 text-xs text-danger" data-testid="pack-unpackable">
                  <WarningCircle size={12} weight="bold" className="inline mr-1" />
                  Could not fit: {plan.unpackable.map((u) => `${u.qty} × ${u.sku}`).join(', ')} — check dimensions in the Packing catalogue.
                </p>
              )}
              {plan.unmatched && plan.unmatched.length > 0 && (
                <p className="mt-2 text-xs text-danger" data-testid="pack-unmatched">
                  <WarningCircle size={12} weight="bold" className="inline mr-1" />
                  Not in packing catalogue (skipped): {plan.unmatched.map((u) => `${u.qty} × ${u.label}`).join(', ')} — add dimensions in Packing catalogue.
                </p>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-2 print:grid-cols-2 print:gap-2" data-testid="pack-carton-grid">
              {(() => {
                // Use a single mm-to-px scale across every carton in the plan
                // so a small carton doesn't appear larger than a Large one
                // beside it. Reference = the biggest carton's longest side.
                const sharedMaxMm = Math.max(
                  ...plan.cartons.map((c) => Math.max(c.lengthMm, c.widthMm))
                );
                return plan.cartons.map((c, i) => (
                  <CartonCard key={`${c.cartonId}-${i}`} carton={c} index={i} sharedMaxMm={sharedMaxMm} />
                ));
              })()}
            </div>

            {/* Hidden container that only shows when the user clicks
                "Print box labels" — one 4"×6" thermal sticker per carton
                for the GC420d printer. See labels-print-mode CSS. */}
            <div className="labels-print-root" data-testid="pack-box-labels-root" aria-hidden="true">
              {plan.cartons.map((c, i) => (
                <BoxLabelSticker
                  key={`label-${i}`}
                  plan={plan}
                  carton={c}
                  index={i}
                  total={plan.cartons.length}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {catalogueOpen && (
        <CatalogueEditor onClose={() => setCatalogueOpen(false)} onRefresh={loadCatalogueSkus} />
      )}
    </div>
  );
}
